import React, { useState } from 'react';

import { LoadingOverlay, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import KiloButton from 'components/KiloButton';
import Table from 'components/Table';
import { useLocalization } from 'context/localization-context';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { CellProps } from 'react-table';
import { PayrollEventRetrieve } from 'types';
import { capitalizeFirstLetter, formatPayrollEventAttendees } from 'utils/formatters';

import { SessionValueBreakdown } from '../../PayrollReport/components/DrawerStaffDetailPayoutReport';
import ModalTotalSessionValueBreakdown from '../../PayrollReport/components/ModalTotalSessionValueBreakdown';

const PayrollReportTable = ({
  tableData,
  isSuccess,
  isError,
  isLoading,
  isFetchingNextPage,
  hasNextPage,
  fetchNextPage,
  ascending,
  handleHeaderClick,
  ordering,
}: {
  tableData: PayrollEventRetrieve[];
  isSuccess: boolean;
  isError: boolean;
  isLoading: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean | undefined;
  fetchNextPage: (options?: any) => Promise<any>;
  ascending: boolean;
  handleHeaderClick: (header: string) => void;
  ordering: string;
}): JSX.Element => {
  const { formatCurrency, timeFormat } = useLocalization();
  const [payrollEventBreakdown, setPayrollEventBreakdown] = useState<SessionValueBreakdown | undefined>();

  const [
    isTotalSessionValueBreakdownModalOpen,
    { open: openTotalSessionValueBreakdownModalOpen, close: closeTotalSessionValueBreakdownModalOpen },
  ] = useDisclosure(false);

  const hasPayrollEventItems = (payrollEventRow: PayrollEventRetrieve): boolean => {
    return payrollEventRow.payrollEventItems.length > 0;
  };

  const handleOpenPayrollReportBreakdownModal = (pe: PayrollEventRetrieve): void => {
    setPayrollEventBreakdown({
      genesisEventName: pe.genesisEventName,
      genesisEventType: pe.genesisEventType,
      payrollEventItems: pe.payrollEventItems,
    });
    openTotalSessionValueBreakdownModalOpen();
  };

  const columns = [
    {
      Header: 'Event',
      accessor: 'genesisEventName' as const,
    },
    {
      Header: 'Event Type',
      accessor: 'genesisEventType' as const,
      Cell: ({ value, row }: CellProps<PayrollEventRetrieve>) => (
        <>
          <Text sx={{ whiteSpace: 'nowrap' }}>{capitalizeFirstLetter(value)}</Text>
          <Text sx={{ whiteSpace: 'nowrap' }}>{formatPayrollEventAttendees(row.original)}</Text>
        </>
      ),
    },
    {
      Header: 'Role',
      accessor: 'roleName' as const,
    },
    {
      Header: 'Pay Rate',
      accessor: 'payRateName' as const,
    },
    {
      Header: 'Payout',
      accessor: 'payoutAmount' as const,
      Cell: ({ value, row }: CellProps<PayrollEventRetrieve>) => {
        if (hasPayrollEventItems(row.original)) {
          return (
            <KiloButton
              rootVariantType='flat'
              variant='subtle'
              onClick={() => handleOpenPayrollReportBreakdownModal(row.original)}
            >
              {formatCurrency(value / 100)}
            </KiloButton>
          );
        }
        return formatCurrency(value / 100);
      },
    },
    {
      Header: 'Date',
      accessor: 'date' as const,
    },
    {
      Header: 'Start',
      accessor: 'time' as const,
      Cell: ({ value }: CellProps<PayrollEventRetrieve>) => {
        dayjs.extend(customParseFormat);
        return dayjs(value, 'HH:mm:ss').format(timeFormat);
      },
    },
    {
      Header: 'Duration',
      accessor: 'duration' as const,
      Cell: ({ value }: CellProps<PayrollEventRetrieve>) => <>{value} min</>,
    },
    {
      Header: 'Reserved',
      accessor: 'athletesReserved' as const,
    },
    {
      Header: 'Attended',
      accessor: 'athletesAttended' as const,
    },
    {
      Header: 'Late Cancel',
      accessor: 'athletesLateCancelled' as const,
      Cell: ({ value }: CellProps<PayrollEventRetrieve>) => <>{value || 0}</>,
    },
    {
      Header: 'No Show',
      accessor: 'athletesNoShow' as const,
      Cell: ({ value }: CellProps<PayrollEventRetrieve>) => <>{value || 0}</>,
    },
  ];

  return (
    <>
      <LoadingOverlay visible={isLoading} />

      <Table
        tableData={tableData}
        columns={columns}
        isSuccess={isSuccess}
        isLoading={isLoading}
        isError={isError}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={!!hasNextPage}
        fetchNextPage={fetchNextPage}
        clickableHeaders={[
          'genesisEventType',
          'genesisEventName',
          'roleName',
          'payRateName',
          'payoutAmount',
          'date',
          'time',
          'duration',
          'athletesAttended',
          'athletesReserved',
        ]}
        handleHeaderClick={handleHeaderClick}
        ordering={ordering}
        ascending={ascending}
      />
      <ModalTotalSessionValueBreakdown
        handleClose={closeTotalSessionValueBreakdownModalOpen}
        opened={isTotalSessionValueBreakdownModalOpen}
        breakdownData={payrollEventBreakdown}
      />
    </>
  );
};

export default PayrollReportTable;
